import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthTokenEncrypted } from '../shared/models/users/auth-token-encrypted';
import { RegisterAsIndividual } from '../shared/models/users/register-as-individual';
import { UserFullDetails } from '../shared/models/users/user-full-details';
import { SearchResult } from '../shared/models/general/search-result';
import { UsersSearchRequest } from '../shared/models/users/users-search-request';
import { UserSearchResult } from '../shared/models/users/user-search-result';
import { UserFiaPins } from '../shared/models/users/user-fia-pins';
import { UserTradingBankAccount } from '../shared/models/users/user-trading-bank-account';
import { UserAitSearchResponse } from '../shared/models/users/user-ait-search-response';
import { TradingAccountWithUserName } from '../shared/models/bank-account/trading-account-with-username';
import { Status } from '../shared/models/general/status';
import { Note } from '../shared/models/note/note';
import { AddSdaReservationRequest } from '../shared/models/users/add-sda-reservation-request';
import { SdaReservation } from '../shared/models/users/sda-reservation';
import { AddFiaReservationRequest } from '../shared/models/users/add-fia-reservation-request';
import { FiaReservation } from '../shared/models/users/fia-reservation';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  env = environment;
  apiUrl = this.env.apiurl + 'platform/users/';
  onboardingUrl = this.env.apiurl + 'platform/onboarding/';

  constructor(
    private http: HttpClient,
    public router: Router
  ) {}

  public RegisterAsIndevidual(model: RegisterAsIndividual): Observable<any> {
    return this.http
      .post<AuthTokenEncrypted>(this.apiUrl + 'register-as-individual', model, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200 || res.status == 204) {
              return new AuthTokenEncrypted().deserialize(res.body);
            }
          }
        })
      );
  }
  //data => new AuthTokenEncrypted().deserialize(data)

  public ConfirmRegistration(Token: string): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + 'confirm-registration/' + Token, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200 || res.status == 204) {
              return;
            }
          }
        })
      );
  }

  public ResendRegistrationEmail(userId: number): Observable<any> {
    return this.http.post<AuthTokenEncrypted>(this.apiUrl + 'resend-registration-email/' + userId, userId, { observe: 'response' }).pipe(
      map((res) => {
        if (res) {
          if (res.status == 200) {
            return;
          }
        }
      })
    );
  }

  public GetUserDetails(Id: number, profileOnly: boolean = false): Observable<UserFullDetails> {
    return this.http.get<UserFullDetails>(`${this.apiUrl}full-details/${Id}/${profileOnly}`);
  }

  public GetFullDetailsByEntity(Entity: number, Id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + 'full-details/entity-type/' + Entity + '/' + Id, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200) {
              return res.body.map((data: any) => new UserFullDetails().deserialize(data));
            }
          }
        })
      );
  }

  public SearchUsers(searchRequest: UsersSearchRequest): Observable<any> {
    return this.http
      .post<any>(this.apiUrl + 'search/', searchRequest, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200) {
              return res.body.map((data: any) => new UserSearchResult().deserialize(data));
            }
          }
        })
      );
  }

  public GetUsersFiaPins(userId: number, excludeExpiredPins: boolean): Observable<UserFiaPins[]> {
    return this.http.get<UserFiaPins[]>(`${this.apiUrl}fia-pins/${userId}/${excludeExpiredPins}`);
  }

  public ValidateToken(Token: string): Observable<any> {
    return this.http.get<any>(this.apiUrl + 'validate-pin/' + Token, { observe: 'response' }).pipe(
      map((res) => {
        if (res) {
          if (res.status == 200) {
            return new SearchResult().deserialize(res.body);
          }
        }
      })
    );
  }

  public UserProfileUpdate(userFullDetails: UserFullDetails): Observable<any> {
    return this.http
      .put<any>(this.apiUrl + 'profile', userFullDetails, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200) {
              return;
            }
          }
        })
      );
  }

  public UserExists(emailAdd: string): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + 'check-user-email/' + emailAdd, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200) {
              return 200;
            }
          }
        })
      );
  }

  public UpdateAllTradeableBankBalances(): Observable<Status> {
    return this.http.post<Status>(`${this.apiUrl}update-tradeable-balances`, null);
  }

  public CancelBankBalanceUpdate(): Observable<Status> {
    return this.http.get<Status>(`${this.apiUrl}cancel-balance-update`);
  }

  public GetListTradeableBankAccounts(): Observable<TradingAccountWithUserName[]> {
    return this.http.get<TradingAccountWithUserName[]>(`${this.apiUrl}tradeable-account-list`);
  }

  public GetLatestBankBalance(userId: number): Observable<any> {
    return this.http
      .get<UserFullDetails>(`${this.apiUrl}latest-bank-balance/${userId}`, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200) {
              return new UserTradingBankAccount().deserialize(res.body);
            }
          }
        })
      );
  }

  public UpdateBankAccountBalance(accountId: number): Observable<UserAitSearchResponse> {
    return this.http
      .get<UserAitSearchResponse>(`${this.apiUrl}latest-bank-balance/${accountId}`, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          if (res) {
            if (res.status == 200) {
              return new UserAitSearchResponse().deserialize(res.body);
            }
          }
        })
      );
  }

  public ManualUpdateBankAccountBalance(accountNumber: number, manualBankBalance: number): Observable<UserAitSearchResponse> {
    return this.http.post<UserAitSearchResponse>(`${this.apiUrl}manual-update-bank-balance/${accountNumber}/${manualBankBalance}`, null);
  }

  public GetIdNumberInUse(idNumber: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}idnumber-in-use/${idNumber}`);
  }

  public ArchiveUser(userId: number, actionByUserId: number): Observable<Note> {
    return this.http.post<Note>(`${this.onboardingUrl}client/archive-and-unonboard`, {
      clientId: userId,
      actionByUserId: actionByUserId,
    });
  }

  public SendIDNumbersForCFSClientCheck(userIdList: number[], actionByUserId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}send-for-cfs-checks`, {
      userIds: userIdList,
      actionByUserId: actionByUserId,
    });
  }

  public SendCFSCode(enteredCode: string, actionByUserId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}send-cfs-security-code`, {
      code: enteredCode,
      requestorUserId: actionByUserId,
    });
  }

  public GetSdaReservations(userId: number): Observable<SdaReservation[]> {
    return this.http.get<SdaReservation[]>(`${this.apiUrl}client-sda-reserved/${userId}`);
  }

  public AddSdaReservation(request: AddSdaReservationRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}client-sda-reserved`, request);
  }

  public GetFiaReservations(userId: number): Observable<FiaReservation[]> {
    return this.http.get<FiaReservation[]>(`${this.apiUrl}client-fia-reserved/${userId}`);
  }

  public AddFiaReservation(request: AddFiaReservationRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}client-fia-reserved`, request);
  }
}
